import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Users, Building2, Building, Phone, Mail, MapPin, Plus, Search, 
  FileText, ShoppingCart, Truck, ClipboardCheck, TrendingUp,
  Calendar, DollarSign, Eye, Edit, Trash2, Send, Check, X,
  ArrowRight, History, Target, Package, Loader2, RefreshCw,
  MessageSquare, UserPlus, Filter, MoreVertical, Map, Sparkles,
  Clock, PhoneCall, Video, MessageCircle, Globe, StickyNote, ListTodo,
  Pen
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import ProspectionH24Tab from '@/components/ProspectionH24Tab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Couleurs pipeline
const PIPELINE_COLORS = {
  prospection: 'bg-slate-400',
  qualification: 'bg-blue-400',
  proposition: 'bg-amber-400',
  negociation: 'bg-orange-400',
  gagne: 'bg-green-500',
  perdu: 'bg-red-500'
};

// Composant carte contact
const ContactCard = ({ contact, onClick, onDelete }) => (
  <Card 
    className="cursor-pointer hover:shadow-md transition-all hover:border-primary"
    onClick={() => onClick(contact)}
  >
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={cn(
            "w-10 h-10 rounded-full flex items-center justify-center text-white font-bold",
            contact.type_contact === 'client' ? 'bg-green-500' :
            contact.type_contact === 'prospect' ? 'bg-blue-500' : 'bg-gray-500'
          )}>
            {((contact.prenom || contact.nom || contact.raison_sociale || '?')[0] || '?').toUpperCase()}
          </div>
          <div>
            <h4 className="font-semibold">
              {contact.prenom || contact.nom 
                ? `${contact.prenom || ''} ${contact.nom || ''}`.trim() 
                : contact.raison_sociale || contact.email || '?'}
            </h4>
            <p className="text-sm text-muted-foreground">{contact.raison_sociale || contact.fonction}</p>
          </div>
        </div>
        <Badge variant={contact.type_contact === 'client' ? 'default' : 'secondary'}>
          {contact.type_contact}
        </Badge>
      </div>
      
      <div className="mt-3 space-y-1 text-sm text-muted-foreground">
        {contact.telephone && (
          <div className="flex items-center gap-2">
            <Phone className="w-3 h-3" />
            {contact.telephone}
          </div>
        )}
        {contact.email && (
          <div className="flex items-center gap-2">
            <Mail className="w-3 h-3" />
            {contact.email}
          </div>
        )}
        {contact.ville && (
          <div className="flex items-center gap-2">
            <MapPin className="w-3 h-3" />
            {contact.ville}
          </div>
        )}
      </div>
      
      <div className="mt-3 pt-3 border-t flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <History className="w-3 h-3" />
          {contact.nb_interactions || 0} interactions
          {contact.nb_interactions > 5 && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">🔥 Actif</Badge>}
        </div>
        {contact.ca_total > 0 && (
          <span className="text-xs font-medium text-green-600">
            CA: {contact.ca_total.toLocaleString()} €
          </span>
        )}
      </div>

      <div className="mt-2 flex justify-end">
        <button
          onClick={(e) => {
            e.stopPropagation();
            const cid = contact.entreprise_id || contact.id;
            if (cid) window.location.href = `/clients/${cid}`;
          }}
          className="text-xs px-2 py-1 rounded bg-primary/10 text-primary hover:bg-primary/20 flex items-center gap-1"
          data-testid={`btn-fiche-360-${contact.id}`}
          title="Voir la fiche 360° complète (parcelles, interventions, devis, communications)"
        >
          🎯 Fiche 360°
        </button>
      </div>
    </CardContent>
  </Card>
);

// Composant carte opportunité (pipeline)
const OpportuniteCard = ({ opp, onDrag, onClick }) => (
  <div
    draggable
    onDragStart={(e) => {
      e.dataTransfer.setData('oppId', opp.id);
      onDrag(opp.id);
    }}
    onClick={() => onClick(opp)}
    className="bg-white dark:bg-gray-800 p-3 rounded-lg border shadow-sm cursor-pointer hover:shadow-md transition-all"
  >
    <div className="flex items-center justify-between mb-2">
      <span className="font-medium text-sm truncate">{opp.titre}</span>
      <span className="text-xs text-muted-foreground">{opp.probabilite}%</span>
    </div>
    <p className="text-xs text-muted-foreground mb-2">{opp.contact_nom}</p>
    <div className="flex items-center justify-between">
      <span className="text-sm font-bold text-green-600">
        {opp.montant_estime?.toLocaleString()} €
      </span>
      {opp.date_cloture_prevue && (
        <span className="text-xs text-muted-foreground">
          {new Date(opp.date_cloture_prevue).toLocaleDateString('fr-FR')}
        </span>
      )}
    </div>
  </div>
);

// Composant colonne pipeline
const PipelineColumn = ({ etape, data, onDrop, onOppClick }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragOver(false);
        const oppId = e.dataTransfer.getData('oppId');
        onDrop(oppId, etape.id);
      }}
      className={cn(
        "flex-1 min-w-[250px] rounded-lg p-3 transition-all",
        isDragOver ? "bg-primary/10 ring-2 ring-primary" : "bg-muted/50"
      )}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={cn("w-3 h-3 rounded-full", PIPELINE_COLORS[etape.id])} />
          <h4 className="font-semibold text-sm">{etape.nom}</h4>
          <Badge variant="secondary" className="text-xs">{data.total}</Badge>
        </div>
        <span className="text-xs font-medium text-green-600">
          {data.montant?.toLocaleString()} €
        </span>
      </div>
      
      <div className="space-y-2 min-h-[200px]">
        {data.opportunites?.map(opp => (
          <OpportuniteCard key={opp.id} opp={opp} onClick={onOppClick} />
        ))}
      </div>
    </div>
  );
};

// Composant ligne devis/commande
const DocumentRow = ({ doc, type, onAction }) => {
  const statusColors = {
    brouillon: 'bg-gray-100 text-gray-700',
    envoye: 'bg-blue-100 text-blue-700',
    accepte: 'bg-green-100 text-green-700',
    refuse: 'bg-red-100 text-red-700',
    en_attente: 'bg-yellow-100 text-yellow-700',
    confirme: 'bg-blue-100 text-blue-700',
    expedie: 'bg-purple-100 text-purple-700',
    livre: 'bg-green-100 text-green-700'
  };
  
  return (
    <div className="flex items-center gap-4 p-4 border rounded-lg hover:bg-muted/50">
      <div className={cn(
        "p-2 rounded-lg",
        type === 'devis' ? 'bg-blue-100 text-blue-600' :
        type === 'commande' ? 'bg-green-100 text-green-600' : 'bg-purple-100 text-purple-600'
      )}>
        {type === 'devis' ? <FileText className="w-5 h-5" /> :
         type === 'commande' ? <ShoppingCart className="w-5 h-5" /> : <Truck className="w-5 h-5" />}
      </div>
      
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium">{doc.numero}</span>
          <Badge className={cn("text-xs", statusColors[doc.statut])}>
            {doc.statut}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">{doc.contact_nom || doc.objet}</p>
      </div>
      
      <div className="text-right">
        <p className="font-bold text-lg">{doc.total_ttc?.toLocaleString()} €</p>
        <p className="text-xs text-muted-foreground">
          {new Date(doc.created_at).toLocaleDateString('fr-FR')}
        </p>
      </div>
      
      <div className="flex gap-1">
        <Button size="icon" variant="ghost" className="h-8 w-8">
          <Eye className="w-4 h-4" />
        </Button>
        {type === 'devis' && doc.statut === 'accepte' && (
          <Button size="icon" variant="ghost" className="h-8 w-8 text-green-600" onClick={() => onAction(doc, 'convertir')}>
            <ArrowRight className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

// Page principale CRM
export default function CRMAdvancedPage() {
  const [activeTab, setActiveTab] = useState('contacts');
  const [loading, setLoading] = useState(true);
  
  // Vue Contacts / Entreprises
  const [viewMode, setViewMode] = useState('contacts'); // 'contacts' ou 'entreprises'
  
  // Data states
  const [contacts, setContacts] = useState([]);
  const [contactsStats, setContactsStats] = useState({});
  const [entreprises, setEntreprises] = useState([]);
  const [entreprisesStats, setEntreprisesStats] = useState({});
  const [pipeline, setPipeline] = useState({});
  const [pipelineStats, setPipelineStats] = useState({});
  const [etapes, setEtapes] = useState([]);
  const [devis, setDevis] = useState([]);
  const [commandes, setCommandes] = useState([]);
  const [articles, setArticles] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  
  // Échanges / Interactions
  const [echanges, setEchanges] = useState([]);
  const [showNewEchangeDialog, setShowNewEchangeDialog] = useState(false);
  const [newEchange, setNewEchange] = useState({
    contact_id: '',
    contact_nom: '',
    type_echange: 'appel', // appel, email, reunion, visite, note
    sujet: '',
    compte_rendu: '',
    date_echange: new Date().toISOString().slice(0, 16),
    duree_minutes: 15,
    prochaine_action: '',
    date_prochaine_action: ''
  });
  
  // Notes et Tâches
  const [showAddNoteDialog, setShowAddNoteDialog] = useState(false);
  const [showAddTaskDialog, setShowAddTaskDialog] = useState(false);
  const [newNote, setNewNote] = useState({ contenu: '' });
  const [newTask, setNewTask] = useState({
    titre: '',
    description: '',
    date_echeance: '',
    priorite: 'normale'
  });
  const [savingNote, setSavingNote] = useState(false);
  const [savingTask, setSavingTask] = useState(false);
  
  // Analyse de marché IA
  const [showAnalyseMarche, setShowAnalyseMarche] = useState(false);
  const [analyseMarche, setAnalyseMarche] = useState({
    produit: '',
    quantite: '',
    unite: 'kg',
    type_vente: 'B2B',
    region: ''
  });
  const [analyseResult, setAnalyseResult] = useState(null);
  const [loadingAnalyse, setLoadingAnalyse] = useState(false);
  const [syncingHubspot, setSyncingHubspot] = useState(false);
  const [hubspotConfigured, setHubspotConfigured] = useState(false);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  
  // Dialog states
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [showEntrepriseDialog, setShowEntrepriseDialog] = useState(false);
  const [showDevisDialog, setShowDevisDialog] = useState(false);
  const [showOpportuniteDialog, setShowOpportuniteDialog] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedEntreprise, setSelectedEntreprise] = useState(null);
  const [showContactDetail, setShowContactDetail] = useState(false);
  const [showEntrepriseDetail, setShowEntrepriseDetail] = useState(false);
  const [contactDetails, setContactDetails] = useState(null);
  const [entrepriseDetails, setEntrepriseDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // Form states
  const [newContact, setNewContact] = useState({
    type_contact: 'prospect',
    nom: '',
    raison_sociale: '',
    email: '',
    telephone: '',
    adresse: '',
    complement_adresse: '',
    code_postal: '',
    ville: '',
    code_ape: '',
    siret: '',
    tva_intra: '',
    latitude: null,
    longitude: null,
    notes: ''
  });
  const [editingContactId, setEditingContactId] = useState(null);
  const [sireneSearching, setSireneSearching] = useState(false);
  const [sireneResults, setSireneResults] = useState([]);
  
  const [newDevis, setNewDevis] = useState({
    contact_id: '',
    objet: '',
    lignes: [{ designation: '', quantite: 1, prix_unitaire_ht: 0, tva: 20 }],
    conditions_paiement: '30 jours',
    notes: ''
  });
  
  const [newEntreprise, setNewEntreprise] = useState({
    nom: '',
    siret: '',
    forme_juridique: '',
    adresse: '',
    code_postal: '',
    ville: '',
    telephone: '',
    email: '',
    type_client: 'prospect',
    secteur_activite: 'agriculture'
  });

  // Fetch functions
  const fetchContacts = useCallback(async () => {
    try {
      const res = await api.get('/crm/contacts', {
        params: { search: searchQuery, type_contact: typeFilter !== 'all' ? typeFilter : undefined }
      });
      setContacts(res.data.contacts || []);
      setContactsStats(res.data.stats || {});
    } catch (error) {
      console.error('Erreur chargement contacts:', error);
    }
  }, [searchQuery, typeFilter]);
  
  const fetchEntreprises = useCallback(async () => {
    try {
      const res = await api.get('/crm-hub/entreprises', {
        params: { search: searchQuery, type_client: typeFilter !== 'all' ? typeFilter : undefined }
      });
      setEntreprises(res.data.entreprises || []);
      setEntreprisesStats({ total: res.data.total || 0 });
    } catch (error) {
      console.error('Erreur chargement entreprises:', error);
      setEntreprises([]);
    }
  }, [searchQuery, typeFilter]);
  
  const fetchPipeline = async () => {
    try {
      const res = await api.get('/crm/pipeline');
      setPipeline(res.data.pipeline || {});
      setEtapes(res.data.etapes || []);
      setPipelineStats(res.data.stats || {});
    } catch (error) {
      console.error('Erreur chargement pipeline:', error);
    }
  };
  
  const fetchDevis = async () => {
    try {
      const res = await api.get('/crm/devis');
      setDevis(res.data.devis || []);
    } catch (error) {
      console.error('Erreur chargement devis:', error);
    }
  };
  
  const fetchCommandes = async () => {
    try {
      const res = await api.get('/crm/bons-commande');
      setCommandes(res.data.bons_commande || []);
    } catch (error) {
      console.error('Erreur chargement commandes:', error);
    }
  };
  
  const fetchArticles = async () => {
    try {
      const res = await api.get('/crm/articles');
      setArticles(res.data.articles || []);
    } catch (error) {
      console.error('Erreur chargement articles:', error);
    }
  };

  // Vérifier la configuration HubSpot
  const checkHubspotConfig = async () => {
    try {
      const res = await api.get('/crm-hub/hubspot/status');
      setHubspotConfigured(res.data.configured || false);
    } catch (error) {
      setHubspotConfigured(false);
    }
  };

  // Synchronisation HubSpot
  const handleSyncHubspot = async () => {
    setSyncingHubspot(true);
    try {
      // D'abord, auto-configurer si pas encore fait
      if (!hubspotConfigured) {
        const configRes = await api.post('/crm-hub/hubspot/auto-configure');
        if (configRes.data.success) {
          setHubspotConfigured(true);
          toast.success(`HubSpot configuré (${configRes.data.source})`);
        }
      }
      
      // Puis synchroniser
      const res = await api.post('/crm-hub/hubspot/sync');
      if (res.data.success) {
        const stats = res.data.stats || {};
        toast.success(
          `Synchronisation réussie ! ${stats.contacts_imported || 0} contacts importés, ${stats.companies_imported || 0} entreprises`
        );
        // Rafraîchir les données
        fetchContacts();
        fetchEntreprises();
      } else {
        toast.error('Erreur lors de la synchronisation');
      }
    } catch (error) {
      console.error('Erreur sync HubSpot:', error);
      const errorMsg = error.response?.data?.detail || 'Erreur de synchronisation HubSpot';
      toast.error(errorMsg);
    } finally {
      setSyncingHubspot(false);
    }
  };
  
  const fetchDashboard = async () => {
    try {
      const res = await api.get('/crm/dashboard');
      setDashboard(res.data);
    } catch (error) {
      console.error('Erreur chargement dashboard:', error);
    }
  };
  
  const fetchEchanges = async () => {
    try {
      const res = await api.get('/crm/interactions');
      setEchanges(res.data.interactions || []);
    } catch (error) {
      console.error('Erreur chargement échanges:', error);
      // Si l'endpoint n'existe pas encore, utiliser données vides
      setEchanges([]);
    }
  };
  
  // Créer un échange/interaction
  const handleCreateEchange = async () => {
    try {
      // Adapter au format attendu par le backend
      const interactionData = {
        contact_id: newEchange.contact_id,
        type_interaction: newEchange.type_echange,
        date: newEchange.date_echange,
        duree_minutes: newEchange.duree_minutes,
        resume: newEchange.compte_rendu || newEchange.sujet,
        suite_a_donner: newEchange.prochaine_action || null,
        date_relance: newEchange.date_prochaine_action || null
      };
      
      await api.post('/crm/interactions', interactionData);
      toast.success('Échange enregistré !');
      setShowNewEchangeDialog(false);
      setNewEchange({
        contact_id: '',
        contact_nom: '',
        type_echange: 'appel',
        sujet: '',
        compte_rendu: '',
        date_echange: new Date().toISOString().slice(0, 16),
        duree_minutes: 15,
        prochaine_action: '',
        date_prochaine_action: ''
      });
      fetchEchanges();
    } catch (error) {
      console.error('Erreur création échange:', error);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };
  
  // Analyse de marché IA
  const handleAnalyseMarche = async () => {
    setLoadingAnalyse(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/crm-hub/market-analysis`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(analyseMarche)
      });
      if (res.ok) {
        const data = await res.json();
        setAnalyseResult(data);
        toast.success('Analyse terminée !');
      } else {
        toast.error('Erreur lors de l\'analyse');
      }
    } catch (error) {
      console.error('Erreur analyse:', error);
      toast.error('Erreur lors de l\'analyse');
    }
    setLoadingAnalyse(false);
  };
  
  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([
      fetchContacts(),
      fetchEntreprises(),
      fetchPipeline(),
      fetchDevis(),
      fetchCommandes(),
      fetchArticles(),
      fetchDashboard(),
      fetchEchanges()
    ]);
    setLoading(false);
  };
  
  useEffect(() => {
    fetchAll();
    checkHubspotConfig();
  }, []);
  
  useEffect(() => {
    fetchContacts();
    fetchEntreprises();
  }, [fetchContacts, fetchEntreprises]);
  
  // Actions
  const handleCreateContact = async () => {
    try {
      if (editingContactId) {
        await api.put(`/crm/contacts/${editingContactId}`, newContact);
        toast.success('Contact modifié');
      } else {
        await api.post('/crm/contacts', newContact);
        toast.success('Contact créé');
      }
      setShowContactDialog(false);
      setEditingContactId(null);
      setSireneResults([]);
      setNewContact({
        type_contact: 'prospect', nom: '', raison_sociale: '', email: '',
        telephone: '', adresse: '', complement_adresse: '', code_postal: '', ville: '',
        code_ape: '', siret: '', tva_intra: '', notes: ''
      });
      fetchContacts();
    } catch (error) {
      toast.error(editingContactId ? 'Erreur modification' : 'Erreur création contact');
    }
  };

  const openEditContact = (c) => {
    setNewContact({
      type_contact: c.type_contact || 'client',
      nom: c.nom || '',
      raison_sociale: c.raison_sociale || '',
      email: c.email || '',
      telephone: c.telephone || '',
      adresse: c.adresse || '',
      complement_adresse: c.complement_adresse || '',
      code_postal: c.code_postal || '',
      ville: c.ville || '',
      code_ape: c.code_ape || '',
      siret: c.siret || '',
      tva_intra: c.tva_intra || '',
      notes: c.notes || '',
    });
    setEditingContactId(c.id);
    setSireneResults([]);
    setShowContactDialog(true);
  };

  const rechercherSireneDialog = async () => {
    const q = (newContact.siret || newContact.raison_sociale || newContact.nom || '').trim();
    if (q.length < 2) { toast.error('Tapez un nom ou SIRET'); return; }
    setSireneSearching(true);
    try {
      const r = await api.get(`/sirene/search?q=${encodeURIComponent(q)}&limit=8`);
      setSireneResults(r.data.results || []);
      if ((r.data.results || []).length === 0) toast.info('Aucune entreprise trouvée');
    } catch (e) {
      toast.error('Erreur API SIRENE');
    } finally {
      setSireneSearching(false);
    }
  };

  const appliquerSireneDialog = (e) => {
    setNewContact({
      ...newContact,
      raison_sociale: e.raison_sociale || e.nom_complet || newContact.raison_sociale,
      nom: newContact.nom || e.raison_sociale || e.nom_complet || '',
      siret: e.siret || newContact.siret,
      tva_intra: e.tva_intra || '',
      adresse: e.adresse || newContact.adresse,
      code_postal: e.code_postal || newContact.code_postal,
      ville: e.ville || newContact.ville,
      code_ape: e.naf_code || newContact.code_ape,
    });
    setSireneResults([]);
    toast.success('Infos SIRET appliquées');
  };
  
  const handleCreateEntreprise = async () => {
    try {
      await api.post('/crm-hub/entreprises', newEntreprise);
      toast.success('Entreprise créée');
      setShowEntrepriseDialog(false);
      setNewEntreprise({
        nom: '', siret: '', forme_juridique: '', adresse: '',
        code_postal: '', ville: '', telephone: '', email: '',
        type_client: 'prospect', secteur_activite: 'agriculture'
      });
      fetchEntreprises();
    } catch (error) {
      toast.error('Erreur création entreprise');
    }
  };
  
  const handleDeleteContact = async (contactId) => {
    if (!confirm('Supprimer ce contact ?')) return;
    try {
      await api.delete(`/crm/contacts/${contactId}`);
      toast.success('Contact supprimé');
      fetchContacts();
    } catch (error) {
      toast.error('Erreur suppression');
    }
  };
  
  const handleMoveOpportunite = async (oppId, nouvelleEtape) => {
    try {
      await api.put(`/crm/opportunites/${oppId}/etape?nouvelle_etape=${nouvelleEtape}`);
      toast.success('Opportunité déplacée');
      fetchPipeline();
    } catch (error) {
      toast.error('Erreur déplacement');
    }
  };
  
  const handleConvertirDevis = async (doc) => {
    try {
      await api.post(`/crm/devis/${doc.id}/convertir-commande`);
      toast.success('Devis converti en commande');
      fetchDevis();
      fetchCommandes();
    } catch (error) {
      toast.error('Erreur conversion');
    }
  };
  
  const handleCreateDevis = async () => {
    try {
      await api.post('/crm/devis', newDevis);
      toast.success('Devis créé');
      setShowDevisDialog(false);
      setNewDevis({
        contact_id: '', objet: '',
        lignes: [{ designation: '', quantite: 1, prix_unitaire_ht: 0, tva: 20 }],
        conditions_paiement: '30 jours', notes: ''
      });
      fetchDevis();
    } catch (error) {
      toast.error('Erreur création devis');
    }
  };
  
  // Ajouter ligne devis
  const addDevisLine = () => {
    setNewDevis({
      ...newDevis,
      lignes: [...newDevis.lignes, { designation: '', quantite: 1, prix_unitaire_ht: 0, tva: 20 }]
    });
  };
  
  // Calculer total devis
  const calculateDevisTotal = () => {
    return newDevis.lignes.reduce((sum, l) => {
      const ht = l.quantite * l.prix_unitaire_ht;
      const ttc = ht * (1 + l.tva / 100);
      return sum + ttc;
    }, 0);
  };

  // Ouvrir le détail d'un contact avec ses notes HubSpot
  const handleContactClick = async (contact) => {
    setSelectedContact(contact);
    setShowContactDetail(true);
    setLoadingDetails(true);
    
    try {
      const response = await api.get(`/crm/contacts/${contact.id}`);
      setContactDetails(response.data);
    } catch (error) {
      console.error('Erreur chargement détails:', error);
      // Fallback: afficher juste le contact sans les détails
      setContactDetails({ contact, notes_hubspot: [], taches_hubspot: [] });
    } finally {
      setLoadingDetails(false);
    }
  };
  
  // Ouvrir le détail d'une entreprise
  const handleEntrepriseClick = async (entreprise) => {
    setSelectedEntreprise(entreprise);
    setShowEntrepriseDetail(true);
    setLoadingDetails(true);
    
    try {
      const response = await api.get(`/crm-hub/entreprises/${entreprise.id}`);
      setEntrepriseDetails(response.data);
    } catch (error) {
      console.error('Erreur chargement détails entreprise:', error);
      setEntrepriseDetails({ entreprise, contacts: [], opportunites: [], interactions: [] });
    } finally {
      setLoadingDetails(false);
    }
  };
  
  // Créer une note pour un contact ou une entreprise
  const handleAddNote = async () => {
    if (!newNote.contenu.trim()) {
      toast.error('Veuillez saisir le contenu de la note');
      return;
    }
    setSavingNote(true);
    try {
      const payload = {
        type_interaction: 'note',
        sujet: 'Note',
        description: newNote.contenu,
        contact_id: selectedContact?.id || null,
        contact_nom: selectedContact ? `${selectedContact.prenom || ''} ${selectedContact.nom || ''}`.trim() : null,
        entreprise_id: selectedEntreprise?.id || null,
        entreprise_nom: selectedEntreprise?.nom || null,
        date_interaction: new Date().toISOString()
      };
      await api.post('/crm-hub/interactions', payload);
      toast.success('Note ajoutée !');
      setShowAddNoteDialog(false);
      setNewNote({ contenu: '' });
      // Rafraîchir les détails
      if (selectedContact) {
        handleContactClick(selectedContact);
      } else if (selectedEntreprise) {
        handleEntrepriseClick(selectedEntreprise);
      }
    } catch (error) {
      console.error('Erreur ajout note:', error);
      toast.error('Erreur lors de l\'ajout de la note');
    } finally {
      setSavingNote(false);
    }
  };
  
  // Créer une tâche pour un contact ou une entreprise
  const handleAddTask = async () => {
    if (!newTask.titre.trim()) {
      toast.error('Veuillez saisir un titre pour la tâche');
      return;
    }
    setSavingTask(true);
    try {
      const payload = {
        type_interaction: 'tache',
        sujet: newTask.titre,
        description: newTask.description,
        contact_id: selectedContact?.id || null,
        contact_nom: selectedContact ? `${selectedContact.prenom || ''} ${selectedContact.nom || ''}`.trim() : null,
        entreprise_id: selectedEntreprise?.id || null,
        entreprise_nom: selectedEntreprise?.nom || null,
        est_tache: true,
        date_echeance: newTask.date_echeance || null,
        priorite: newTask.priorite,
        statut_tache: 'a_faire',
        date_interaction: new Date().toISOString()
      };
      await api.post('/crm-hub/interactions', payload);
      toast.success('Tâche créée !');
      setShowAddTaskDialog(false);
      setNewTask({ titre: '', description: '', date_echeance: '', priorite: 'normale' });
      // Rafraîchir les détails
      if (selectedContact) {
        handleContactClick(selectedContact);
      } else if (selectedEntreprise) {
        handleEntrepriseClick(selectedEntreprise);
      }
    } catch (error) {
      console.error('Erreur ajout tâche:', error);
      toast.error('Erreur lors de la création de la tâche');
    } finally {
      setSavingTask(false);
    }
  };

  if (loading) {
    return (
      <div className="h-[calc(100vh-200px)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="crm-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Users className="w-8 h-8" />
            CRM Commercial
          </h1>
          <p className="text-muted-foreground">
            Contacts, opportunités, devis, commandes et livraisons
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button onClick={() => setShowContactDialog(true)}>
            <UserPlus className="w-4 h-4 mr-2" />
            Nouveau contact
          </Button>
          <Button variant="outline" onClick={() => setShowDevisDialog(true)}>
            <FileText className="w-4 h-4 mr-2" />
            Nouveau devis
          </Button>
          <Button variant="ghost" onClick={fetchAll}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {/* KPIs Dashboard */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-4">
              <Users className="w-6 h-6 mb-2 opacity-80" />
              <p className="text-3xl font-bold">{dashboard.stats.total_contacts}</p>
              <p className="text-xs text-blue-100">Contacts</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
            <CardContent className="p-4">
              <Building2 className="w-6 h-6 mb-2 opacity-80" />
              <p className="text-3xl font-bold">{dashboard.stats.total_entreprises || 0}</p>
              <p className="text-xs text-indigo-100">Entreprises</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-4">
              <Target className="w-6 h-6 mb-2 opacity-80" />
              <p className="text-3xl font-bold">{dashboard.stats.opportunites_ouvertes}</p>
              <p className="text-xs text-purple-100">Opportunités</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
            <CardContent className="p-4">
              <TrendingUp className="w-6 h-6 mb-2 opacity-80" />
              <p className="text-3xl font-bold">{(dashboard.stats.montant_pipeline / 1000).toFixed(0)}k€</p>
              <p className="text-xs text-amber-100">Pipeline</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-4">
              <FileText className="w-6 h-6 mb-2 opacity-80" />
              <p className="text-3xl font-bold">{dashboard.stats.devis_mois}</p>
              <p className="text-xs text-green-100">Devis ce mois</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-cyan-500 to-cyan-600 text-white">
            <CardContent className="p-4">
              <ShoppingCart className="w-6 h-6 mb-2 opacity-80" />
              <p className="text-3xl font-bold">{dashboard.stats.commandes_mois}</p>
              <p className="text-xs text-cyan-100">Commandes ce mois</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
            <CardContent className="p-4">
              <DollarSign className="w-6 h-6 mb-2 opacity-80" />
              <p className="text-3xl font-bold">{(dashboard.stats.ca_mois / 1000).toFixed(1)}k€</p>
              <p className="text-xs text-emerald-100">CA ce mois</p>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Boutons d'actions rapides */}
      <div className="flex gap-2 flex-wrap">
        <Button onClick={() => setShowContactDialog(true)} className="bg-blue-600 hover:bg-blue-700">
          <UserPlus className="w-4 h-4 mr-2" />
          Nouveau contact
        </Button>
        <Button variant="outline" onClick={() => setShowNewEchangeDialog(true)}>
          <PhoneCall className="w-4 h-4 mr-2" />
          Nouvel échange
        </Button>
        <Button variant="outline" onClick={() => setShowDevisDialog(true)}>
          <FileText className="w-4 h-4 mr-2" />
          Nouveau devis
        </Button>
        <Button variant="outline" onClick={() => setShowAnalyseMarche(true)}>
          <Sparkles className="w-4 h-4 mr-2" />
          Analyse marché IA
        </Button>
        <Button 
          variant="outline" 
          onClick={handleSyncHubspot}
          disabled={syncingHubspot}
          className="border-orange-300 text-orange-700 hover:bg-orange-50"
          data-testid="sync-hubspot-btn"
        >
          {syncingHubspot ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4 mr-2" />
          )}
          {syncingHubspot ? 'Synchronisation...' : 'Sync HubSpot'}
        </Button>
      </div>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-7 w-full max-w-4xl">
          <TabsTrigger value="contacts" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline">Contacts</span>
          </TabsTrigger>
          <TabsTrigger value="echanges" className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            <span className="hidden sm:inline">Échanges</span>
          </TabsTrigger>
          <TabsTrigger value="pipeline" className="flex items-center gap-2">
            <Target className="w-4 h-4" />
            <span className="hidden sm:inline">Pipeline</span>
          </TabsTrigger>
          <TabsTrigger value="devis" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            <span className="hidden sm:inline">Devis</span>
          </TabsTrigger>
          <TabsTrigger value="commandes" className="flex items-center gap-2">
            <ShoppingCart className="w-4 h-4" />
            <span className="hidden sm:inline">Commandes</span>
          </TabsTrigger>
          <TabsTrigger value="articles" className="flex items-center gap-2">
            <Package className="w-4 h-4" />
            <span className="hidden sm:inline">Articles</span>
          </TabsTrigger>
          <TabsTrigger value="carte" className="flex items-center gap-2">
            <Map className="w-4 h-4" />
            <span className="hidden sm:inline">Carte</span>
          </TabsTrigger>
          <TabsTrigger value="prospection-h24" className="flex items-center gap-2" data-testid="tab-prospection-h24">
            <Target className="w-4 h-4" />
            <span className="hidden sm:inline">Prospection IA</span>
          </TabsTrigger>
        </TabsList>
        
        {/* Tab Contacts / Entreprises */}
        <TabsContent value="contacts" className="space-y-4">
          {/* Sélecteur Contacts / Entreprises */}
          <div className="flex flex-wrap gap-4 items-center">
            <Select value={viewMode} onValueChange={setViewMode}>
              <SelectTrigger className="w-48" data-testid="view-mode-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="contacts">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Contacts
                  </div>
                </SelectItem>
                <SelectItem value="entreprises">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Entreprises
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={viewMode === 'contacts' ? "Rechercher un contact..." : "Rechercher une entreprise..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-input"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="client">Clients</SelectItem>
                <SelectItem value="prospect">Prospects</SelectItem>
                {viewMode === 'contacts' && <SelectItem value="fournisseur">Fournisseurs</SelectItem>}
              </SelectContent>
            </Select>
            
            {/* Bouton d'ajout dynamique */}
            {viewMode === 'contacts' ? (
              <Button onClick={() => setShowContactDialog(true)} data-testid="add-contact-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                Nouveau contact
              </Button>
            ) : (
              <Button onClick={() => setShowEntrepriseDialog(true)} data-testid="add-entreprise-btn">
                <Building2 className="w-4 h-4 mr-2" />
                Nouvelle entreprise
              </Button>
            )}
          </div>
          
          {/* Affichage Contacts */}
          {viewMode === 'contacts' && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {contacts.map(contact => (
                <ContactCard
                  key={contact.id}
                  contact={contact}
                  onClick={handleContactClick}
                  onDelete={handleDeleteContact}
                />
              ))}
              {contacts.length === 0 && (
                <div className="col-span-3 text-center py-12 text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Aucun contact trouvé</p>
                  <Button variant="link" onClick={() => setShowContactDialog(true)}>
                    Créer un contact
                  </Button>
                </div>
              )}
            </div>
          )}
          
          {/* Affichage Entreprises */}
          {viewMode === 'entreprises' && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {entreprises.map(entreprise => (
                <Card 
                  key={entreprise.id}
                  className="cursor-pointer hover:shadow-md transition-all hover:border-primary"
                  onClick={() => handleEntrepriseClick(entreprise)}
                  data-testid={`entreprise-card-${entreprise.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-10 h-10 rounded-full flex items-center justify-center text-white font-bold",
                          entreprise.type_client === 'client' ? 'bg-green-500' :
                          entreprise.type_client === 'prospect' ? 'bg-blue-500' : 'bg-gray-500'
                        )}>
                          <Building2 className="w-5 h-5" />
                        </div>
                        <div>
                          <h4 className="font-semibold">{entreprise.nom}</h4>
                          <p className="text-sm text-muted-foreground">
                            {entreprise.forme_juridique} {entreprise.secteur_activite && `• ${entreprise.secteur_activite}`}
                          </p>
                        </div>
                      </div>
                      <Badge variant={entreprise.type_client === 'client' ? 'default' : 'secondary'}>
                        {entreprise.type_client}
                      </Badge>
                    </div>
                    
                    <div className="mt-3 space-y-1 text-sm text-muted-foreground">
                      {entreprise.telephone && (
                        <div className="flex items-center gap-2">
                          <Phone className="w-3 h-3" />
                          {entreprise.telephone}
                        </div>
                      )}
                      {entreprise.email && (
                        <div className="flex items-center gap-2">
                          <Mail className="w-3 h-3" />
                          {entreprise.email}
                        </div>
                      )}
                      {entreprise.ville && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-3 h-3" />
                          {entreprise.ville}
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-3 pt-3 border-t flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Users className="w-3 h-3" />
                        {entreprise.nb_contacts || 0} contacts
                      </div>
                      {entreprise.ca_total > 0 && (
                        <span className="text-xs font-medium text-green-600">
                          CA: {entreprise.ca_total.toLocaleString()} €
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {entreprises.length === 0 && (
                <div className="col-span-3 text-center py-12 text-muted-foreground">
                  <Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Aucune entreprise trouvée</p>
                  <Button variant="link" onClick={() => setShowEntrepriseDialog(true)}>
                    Créer une entreprise
                  </Button>
                </div>
              )}
            </div>
          )}
        </TabsContent>
        
        {/* Tab Pipeline */}
        <TabsContent value="pipeline" className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Badge variant="secondary" className="text-lg px-4 py-2">
                Pipeline: {pipelineStats.montant_total?.toLocaleString()} €
              </Badge>
              <Badge variant="outline">
                Pondéré: {pipelineStats.montant_pondere?.toLocaleString()} €
              </Badge>
            </div>
            <Button onClick={() => setShowOpportuniteDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle opportunité
            </Button>
          </div>
          
          <ScrollArea className="w-full">
            <div className="flex gap-4 pb-4 min-w-max">
              {etapes.filter(e => e.id !== 'perdu').map(etape => (
                <PipelineColumn
                  key={etape.id}
                  etape={etape}
                  data={pipeline[etape.id] || { opportunites: [], total: 0, montant: 0 }}
                  onDrop={handleMoveOpportunite}
                  onOppClick={() => {}}
                />
              ))}
            </div>
          </ScrollArea>
        </TabsContent>
        
        {/* Tab Devis */}
        <TabsContent value="devis" className="space-y-4">
          <div className="space-y-2">
            {devis.map(d => (
              <DocumentRow
                key={d.id}
                doc={d}
                type="devis"
                onAction={handleConvertirDevis}
              />
            ))}
            {devis.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Aucun devis</p>
                <Button variant="link" onClick={() => setShowDevisDialog(true)}>
                  Créer un devis
                </Button>
              </div>
            )}
          </div>
        </TabsContent>
        
        {/* Tab Commandes */}
        <TabsContent value="commandes" className="space-y-4">
          <div className="space-y-2">
            {commandes.map(c => (
              <DocumentRow
                key={c.id}
                doc={c}
                type="commande"
                onAction={() => {}}
              />
            ))}
            {commandes.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Aucune commande</p>
              </div>
            )}
          </div>
        </TabsContent>
        
        {/* Tab Articles */}
        <TabsContent value="articles" className="space-y-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold">Catalogue articles</h3>
            <Button onClick={() => toast.info('Création d\'article à implémenter')}>
              <Plus className="w-4 h-4 mr-2" />
              Nouvel article
            </Button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {articles.map(article => (
              <Card key={article.id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <Badge variant="secondary" className="text-xs mb-2">{article.reference}</Badge>
                      <h4 className="font-semibold">{article.nom}</h4>
                      <p className="text-sm text-muted-foreground">{article.categorie}</p>
                    </div>
                    {article.photo_url && (
                      <img src={article.photo_url} alt={article.nom} className="w-16 h-16 object-cover rounded" />
                    )}
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-lg font-bold">{article.prix_ht} € HT</span>
                    <span className="text-sm text-muted-foreground">/{article.unite}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
            {articles.length === 0 && (
              <div className="col-span-3 text-center py-12 text-muted-foreground">
                <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Aucun article</p>
              </div>
            )}
          </div>
        </TabsContent>
        
        {/* Tab Échanges - Suivi des interactions */}
        <TabsContent value="echanges" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold">Historique des échanges</h3>
            <Button onClick={() => setShowNewEchangeDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Nouvel échange
            </Button>
          </div>
          
          <div className="space-y-3">
            {echanges.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="p-8 text-center">
                  <MessageSquare className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-30" />
                  <h4 className="font-medium mb-2">Aucun échange enregistré</h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    Enregistrez vos appels, réunions et échanges avec vos contacts
                  </p>
                  <Button onClick={() => setShowNewEchangeDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Créer le premier échange
                  </Button>
                </CardContent>
              </Card>
            ) : (
              echanges.map((echange) => {
                const typeIcons = {
                  appel: PhoneCall,
                  email: Mail,
                  reunion: Video,
                  visite: MapPin,
                  salon: Users,
                  note: MessageCircle
                };
                // Le backend utilise type_interaction
                const TypeIcon = typeIcons[echange.type_interaction] || MessageSquare;
                const typeColors = {
                  appel: 'bg-blue-100 text-blue-700',
                  email: 'bg-purple-100 text-purple-700',
                  reunion: 'bg-green-100 text-green-700',
                  visite: 'bg-orange-100 text-orange-700',
                  salon: 'bg-yellow-100 text-yellow-700',
                  note: 'bg-gray-100 text-gray-700'
                };
                
                return (
                  <Card key={echange.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <div className={cn("p-3 rounded-lg", typeColors[echange.type_interaction] || 'bg-gray-100')}>
                          <TypeIcon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <h4 className="font-semibold truncate">{echange.resume}</h4>
                            {echange.duree_minutes && (
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {echange.duree_minutes} min
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">{echange.contact_nom}</p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-xs text-muted-foreground">
                              {new Date(echange.date).toLocaleDateString('fr-FR', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                            {echange.suite_a_donner && (
                              <Badge variant="outline" className="text-xs">
                                À faire : {echange.suite_a_donner}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </TabsContent>
        
        {/* Tab Carte - Visualisation géographique */}
        <TabsContent value="carte" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Map className="w-5 h-5" />
                Carte des contacts
              </CardTitle>
              <CardDescription>
                Visualisez vos clients et prospects sur une carte
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-muted/50 rounded-lg h-[500px] flex items-center justify-center">
                <div className="text-center">
                  <Globe className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-30" />
                  <h4 className="font-medium mb-2">Carte des contacts</h4>
                  <p className="text-sm text-muted-foreground mb-4 max-w-md">
                    Pour afficher vos contacts sur la carte, assurez-vous que leurs adresses 
                    sont renseignées avec les coordonnées GPS (latitude/longitude).
                  </p>
                  <div className="flex flex-wrap gap-4 justify-center">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-green-500" />
                      <span className="text-sm">Clients ({contacts.filter(c => c.type_contact === 'client').length})</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-blue-500" />
                      <span className="text-sm">Prospects ({contacts.filter(c => c.type_contact === 'prospect').length})</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-gray-500" />
                      <span className="text-sm">Fournisseurs ({contacts.filter(c => c.type_contact === 'fournisseur').length})</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Prospection IA H24 */}
        <TabsContent value="prospection-h24" className="space-y-4">
          <ProspectionH24Tab />
        </TabsContent>
      </Tabs>
      <Dialog open={showNewEchangeDialog} onOpenChange={setShowNewEchangeDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Nouvel échange
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type d'échange *</Label>
                <Select 
                  value={newEchange.type_echange}
                  onValueChange={(v) => setNewEchange({...newEchange, type_echange: v})}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="appel">📞 Appel téléphonique</SelectItem>
                    <SelectItem value="email">📧 Email</SelectItem>
                    <SelectItem value="reunion">🎥 Réunion / Visio</SelectItem>
                    <SelectItem value="visite">📍 Visite sur site</SelectItem>
                    <SelectItem value="note">📝 Note interne</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Contact *</Label>
                <Select 
                  value={newEchange.contact_id}
                  onValueChange={(v) => {
                    const contact = contacts.find(c => c.id === v);
                    setNewEchange({
                      ...newEchange, 
                      contact_id: v,
                      contact_nom: contact ? `${contact.prenom || ''} ${contact.nom || ''}`.trim() || contact.raison_sociale || '' : ''
                    });
                  }}
                >
                  <SelectTrigger><SelectValue placeholder="Choisir un contact" /></SelectTrigger>
                  <SelectContent>
                    {contacts.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.prenom || c.nom ? `${c.prenom || ''} ${c.nom || ''}`.trim() : c.raison_sociale || c.email || '?'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Sujet *</Label>
              <Input
                value={newEchange.sujet}
                onChange={(e) => setNewEchange({...newEchange, sujet: e.target.value})}
                placeholder="Ex: Présentation catalogue, Négociation prix..."
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date et heure</Label>
                <Input
                  type="datetime-local"
                  value={newEchange.date_echange}
                  onChange={(e) => setNewEchange({...newEchange, date_echange: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Durée (minutes)</Label>
                <Input
                  type="number"
                  value={newEchange.duree_minutes}
                  onChange={(e) => setNewEchange({...newEchange, duree_minutes: parseInt(e.target.value) || 15})}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Compte rendu</Label>
              <Textarea
                value={newEchange.compte_rendu}
                onChange={(e) => setNewEchange({...newEchange, compte_rendu: e.target.value})}
                placeholder="Ce qui a été discuté, décisions prises..."
                rows={4}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prochaine action</Label>
                <Input
                  value={newEchange.prochaine_action}
                  onChange={(e) => setNewEchange({...newEchange, prochaine_action: e.target.value})}
                  placeholder="Ex: Envoyer devis, Rappeler..."
                />
              </div>
              <div className="space-y-2">
                <Label>Date prochaine action</Label>
                <Input
                  type="date"
                  value={newEchange.date_prochaine_action}
                  onChange={(e) => setNewEchange({...newEchange, date_prochaine_action: e.target.value})}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewEchangeDialog(false)}>Annuler</Button>
            <Button onClick={handleCreateEchange} disabled={!newEchange.contact_id || !newEchange.sujet}>
              <Check className="w-4 h-4 mr-2" />
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog Analyse de Marché IA */}
      <Dialog open={showAnalyseMarche} onOpenChange={setShowAnalyseMarche}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-yellow-500" />
              Analyse de marché IA
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Produit à analyser *</Label>
              <Input
                value={analyseMarche.produit}
                onChange={(e) => setAnalyseMarche({...analyseMarche, produit: e.target.value})}
                placeholder="Ex: Lavande, Huile d'olive, Miel..."
              />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Quantité</Label>
                <Input
                  type="number"
                  value={analyseMarche.quantite}
                  onChange={(e) => setAnalyseMarche({...analyseMarche, quantite: e.target.value})}
                  placeholder="100"
                />
              </div>
              <div className="space-y-2">
                <Label>Unité</Label>
                <Select 
                  value={analyseMarche.unite}
                  onValueChange={(v) => setAnalyseMarche({...analyseMarche, unite: v})}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kg">kg</SelectItem>
                    <SelectItem value="L">litres</SelectItem>
                    <SelectItem value="t">tonnes</SelectItem>
                    <SelectItem value="unites">unités</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Type de vente</Label>
                <Select 
                  value={analyseMarche.type_vente}
                  onValueChange={(v) => setAnalyseMarche({...analyseMarche, type_vente: v})}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="B2B">B2B (pro)</SelectItem>
                    <SelectItem value="B2C">B2C (particuliers)</SelectItem>
                    <SelectItem value="mixte">Mixte</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Région cible</Label>
              <Input
                value={analyseMarche.region}
                onChange={(e) => setAnalyseMarche({...analyseMarche, region: e.target.value})}
                placeholder="Ex: Provence, France entière..."
              />
            </div>
            
            <Button 
              className="w-full" 
              onClick={handleAnalyseMarche}
              disabled={loadingAnalyse || !analyseMarche.produit}
            >
              {loadingAnalyse ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Analyse en cours...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Lancer l'analyse IA
                </>
              )}
            </Button>
            
            {analyseResult && (
              <div className="space-y-4 mt-4">
                {/* Prix et tendance */}
                <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 border-green-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Prix moyen estimé</p>
                        <p className="text-3xl font-bold text-green-700">{analyseResult.prix_moyen} €/{analyseMarche.unite}</p>
                      </div>
                      <Badge 
                        className={`text-lg px-4 py-2 ${
                          analyseResult.tendance === 'hausse' ? 'bg-green-500' : 
                          analyseResult.tendance === 'baisse' ? 'bg-red-500' : 'bg-gray-500'
                        }`}
                      >
                        {analyseResult.tendance === 'hausse' ? '📈' : analyseResult.tendance === 'baisse' ? '📉' : '➡️'} {analyseResult.tendance}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Fourchette de prix */}
                {analyseResult.fourchette_prix && (
                  <Card>
                    <CardContent className="p-4">
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4" />
                        Fourchette de prix du marché
                      </h4>
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div className="p-3 bg-red-50 dark:bg-red-950/20 rounded-lg">
                          <p className="text-xs text-muted-foreground">Prix bas</p>
                          <p className="text-xl font-bold text-red-600">{analyseResult.fourchette_prix.min} €</p>
                        </div>
                        <div className="p-3 bg-green-50 dark:bg-green-950/20 rounded-lg">
                          <p className="text-xs text-muted-foreground">Prix moyen</p>
                          <p className="text-xl font-bold text-green-600">{analyseResult.fourchette_prix.moyen} €</p>
                        </div>
                        <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
                          <p className="text-xs text-muted-foreground">Prix haut</p>
                          <p className="text-xl font-bold text-blue-600">{analyseResult.fourchette_prix.max} €</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Acheteurs potentiels */}
                {analyseResult.acheteurs && analyseResult.acheteurs.length > 0 && (
                  <Card>
                    <CardContent className="p-4">
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <Building className="w-4 h-4" />
                        Acheteurs potentiels identifiés
                      </h4>
                      <div className="space-y-2">
                        {analyseResult.acheteurs.map((acheteur, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                            <div>
                              <p className="font-medium">{acheteur.nom}</p>
                              <p className="text-xs text-muted-foreground">{acheteur.type} • {acheteur.localisation}</p>
                            </div>
                            {acheteur.prix_indicatif && (
                              <Badge variant="outline">{acheteur.prix_indicatif} €/{analyseMarche.unite}</Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Recommandations */}
                {analyseResult.recommandations && (
                  <Card className="bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-950/20 dark:to-orange-950/20 border-yellow-200">
                    <CardContent className="p-4">
                      <h4 className="font-semibold mb-2 flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-yellow-600" />
                        Recommandations IA
                      </h4>
                      <p className="text-sm">{analyseResult.recommandations}</p>
                    </CardContent>
                  </Card>
                )}
                
                {/* Analyse détaillée */}
                {analyseResult.analyse && (
                  <Card>
                    <CardContent className="p-4">
                      <h4 className="font-semibold mb-2 flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Analyse détaillée du marché
                      </h4>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">{analyseResult.analyse}</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAnalyseMarche(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog Nouveau Contact */}
      <Dialog open={showContactDialog} onOpenChange={(v) => {
        setShowContactDialog(v);
        if (!v) { setEditingContactId(null); setSireneResults([]); }
      }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingContactId ? 'Modifier le contact' : 'Nouveau contact'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select 
                  value={newContact.type_contact}
                  onValueChange={(v) => setNewContact({...newContact, type_contact: v})}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="prospect">Prospect</SelectItem>
                    <SelectItem value="client">Client</SelectItem>
                    <SelectItem value="fournisseur">Fournisseur</SelectItem>
                    <SelectItem value="client_fournisseur">Client + Fournisseur</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Raison sociale</Label>
                <Input
                  value={newContact.raison_sociale}
                  onChange={(e) => setNewContact({...newContact, raison_sociale: e.target.value})}
                  placeholder="Nom de l'entreprise"
                />
              </div>
            </div>

            {/* Bouton auto-SIRET */}
            <div className="flex items-end gap-2">
              <div className="flex-1 space-y-2">
                <Label>SIRET (14 chiffres)</Label>
                <Input
                  value={newContact.siret}
                  onChange={(e) => setNewContact({...newContact, siret: e.target.value.replace(/\s/g, '')})}
                  placeholder="552100554 00054"
                  maxLength={14}
                  data-testid="crm-contact-siret"
                />
              </div>
              <Button type="button" variant="outline" onClick={rechercherSireneDialog} disabled={sireneSearching}
                data-testid="crm-btn-search-sirene">
                {sireneSearching ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Search className="w-4 h-4 mr-1" />}
                Remplir auto
              </Button>
            </div>
            {sireneResults.length > 0 && (
              <div className="max-h-44 overflow-y-auto border rounded-md divide-y" data-testid="crm-sirene-results">
                {sireneResults.map((r, i) => (
                  <button key={i} type="button" onClick={() => appliquerSireneDialog(r)}
                    className="w-full text-left px-3 py-2 hover:bg-muted text-sm">
                    <div className="font-medium">{r.raison_sociale || r.nom_complet}</div>
                    <div className="text-xs text-muted-foreground">SIRET {r.siret} — {r.adresse}, {r.code_postal} {r.ville}</div>
                  </button>
                ))}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nom *</Label>
                <Input
                  value={newContact.nom}
                  onChange={(e) => setNewContact({...newContact, nom: e.target.value})}
                  data-testid="crm-contact-nom"
                />
              </div>
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <Input
                  value={newContact.telephone}
                  onChange={(e) => setNewContact({...newContact, telephone: e.target.value})}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={newContact.email}
                onChange={(e) => setNewContact({...newContact, email: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label>Adresse</Label>
              <Input
                value={newContact.adresse}
                onChange={(e) => setNewContact({...newContact, adresse: e.target.value})}
                placeholder="N° et voie"
                data-testid="crm-contact-adresse"
              />
            </div>

            <div className="space-y-2">
              <Label>Complément d'adresse</Label>
              <Input
                value={newContact.complement_adresse}
                onChange={(e) => setNewContact({...newContact, complement_adresse: e.target.value})}
                placeholder="Bâtiment, étage, lieu-dit..."
                data-testid="crm-contact-complement"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Code postal</Label>
                <Input
                  value={newContact.code_postal}
                  onChange={(e) => setNewContact({...newContact, code_postal: e.target.value})}
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Ville</Label>
                <Input
                  value={newContact.ville}
                  onChange={(e) => setNewContact({...newContact, ville: e.target.value})}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Code APE</Label>
                <Input
                  value={newContact.code_ape}
                  onChange={(e) => setNewContact({...newContact, code_ape: e.target.value})}
                  placeholder="Ex: 01.21Z"
                />
              </div>
              <div className="space-y-2">
                <Label>N° TVA Intracom. <span className="text-xs text-muted-foreground">(auto depuis SIRET)</span></Label>
                <Input
                  value={newContact.tva_intra}
                  onChange={(e) => setNewContact({...newContact, tva_intra: e.target.value})}
                  placeholder="FR12 345678901"
                  data-testid="crm-contact-tva"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContactDialog(false)}>Annuler</Button>
            <Button onClick={handleCreateContact} disabled={!newContact.nom} data-testid="crm-btn-save-contact">
              {editingContactId ? (
                <><Pen className="w-4 h-4 mr-2" />Enregistrer</>
              ) : (
                <><Plus className="w-4 h-4 mr-2" />Créer</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog Nouveau Devis */}
      <Dialog open={showDevisDialog} onOpenChange={setShowDevisDialog}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Nouveau devis</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Client *</Label>
                <Select 
                  value={newDevis.contact_id}
                  onValueChange={(v) => setNewDevis({...newDevis, contact_id: v})}
                >
                  <SelectTrigger><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                  <SelectContent>
                    {contacts.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.prenom || c.nom ? `${c.prenom || ''} ${c.nom || ''}`.trim() : c.raison_sociale || c.email || '?'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Objet *</Label>
                <Input
                  value={newDevis.objet}
                  onChange={(e) => setNewDevis({...newDevis, objet: e.target.value})}
                  placeholder="Ex: Prestation vendanges"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Lignes</Label>
                <Button type="button" variant="ghost" size="sm" onClick={addDevisLine}>
                  <Plus className="w-4 h-4 mr-1" />
                  Ajouter
                </Button>
              </div>
              
              <div className="border rounded-lg p-3 space-y-2">
                {newDevis.lignes.map((ligne, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                    <Input
                      className="col-span-5"
                      placeholder="Désignation"
                      value={ligne.designation}
                      onChange={(e) => {
                        const lignes = [...newDevis.lignes];
                        lignes[idx].designation = e.target.value;
                        setNewDevis({...newDevis, lignes});
                      }}
                    />
                    <Input
                      className="col-span-2"
                      type="number"
                      placeholder="Qté"
                      value={ligne.quantite}
                      onChange={(e) => {
                        const lignes = [...newDevis.lignes];
                        lignes[idx].quantite = parseFloat(e.target.value) || 0;
                        setNewDevis({...newDevis, lignes});
                      }}
                    />
                    <Input
                      className="col-span-3"
                      type="number"
                      placeholder="Prix HT"
                      value={ligne.prix_unitaire_ht}
                      onChange={(e) => {
                        const lignes = [...newDevis.lignes];
                        lignes[idx].prix_unitaire_ht = parseFloat(e.target.value) || 0;
                        setNewDevis({...newDevis, lignes});
                      }}
                    />
                    <span className="col-span-2 text-right font-medium">
                      {(ligne.quantite * ligne.prix_unitaire_ht * 1.2).toFixed(2)} €
                    </span>
                  </div>
                ))}
              </div>
              
              <div className="text-right font-bold text-lg">
                Total TTC: {calculateDevisTotal().toFixed(2)} €
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDevisDialog(false)}>Annuler</Button>
            <Button onClick={handleCreateDevis} disabled={!newDevis.contact_id || !newDevis.objet}>
              <FileText className="w-4 h-4 mr-2" />
              Créer le devis
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Détail Contact avec Notes HubSpot */}
      <Dialog open={showContactDetail} onOpenChange={setShowContactDetail}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-start justify-between gap-3">
              <DialogTitle className="flex items-center gap-3 flex-1">
                <div className={cn(
                  "w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg",
                  selectedContact?.type_contact === 'client' ? 'bg-green-500' :
                  selectedContact?.type_contact === 'prospect' ? 'bg-blue-500' : 'bg-gray-500'
                )}>
                  {((selectedContact?.prenom || selectedContact?.nom || selectedContact?.raison_sociale || '?')[0] || '?').toUpperCase()}
                </div>
                <div>
                  <span className="text-xl">
                    {selectedContact?.prenom || selectedContact?.nom 
                      ? `${selectedContact?.prenom || ''} ${selectedContact?.nom || ''}`.trim() 
                      : selectedContact?.raison_sociale || selectedContact?.email || 'Contact'}
                  </span>
                  {selectedContact?.raison_sociale && (selectedContact?.prenom || selectedContact?.nom) && (
                    <p className="text-sm text-muted-foreground font-normal">{selectedContact.raison_sociale}</p>
                  )}
                </div>
              </DialogTitle>
              <Button size="sm" variant="outline" onClick={() => {
                setShowContactDetail(false);
                openEditContact(selectedContact);
              }} data-testid="btn-edit-contact">
                <Pen className="w-4 h-4 mr-1" />
                Modifier
              </Button>
            </div>
          </DialogHeader>

          {loadingDetails ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Infos contact */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                {selectedContact?.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <a href={`mailto:${selectedContact.email}`} className="text-blue-600 hover:underline">
                      {selectedContact.email}
                    </a>
                  </div>
                )}
                {selectedContact?.telephone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <a href={`tel:${selectedContact.telephone}`} className="hover:underline">
                      {selectedContact.telephone}
                    </a>
                  </div>
                )}
                {selectedContact?.mobile && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    {selectedContact.mobile}
                  </div>
                )}
                {(selectedContact?.ville || selectedContact?.adresse) && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    {[selectedContact?.adresse, selectedContact?.code_postal, selectedContact?.ville].filter(Boolean).join(', ')}
                  </div>
                )}
                {selectedContact?.fonction && (
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-muted-foreground" />
                    {selectedContact.fonction}
                  </div>
                )}
              </div>

              {/* Notes HubSpot */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold flex items-center gap-2">
                    <StickyNote className="w-5 h-5" />
                    Notes ({contactDetails?.notes_hubspot?.length || 0})
                  </h3>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setShowAddNoteDialog(true)}
                    data-testid="add-note-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Ajouter une note
                  </Button>
                </div>
                {contactDetails?.notes_hubspot?.length > 0 ? (
                  <div className="space-y-3">
                    {contactDetails.notes_hubspot.map((note, idx) => (
                      <Card key={idx} className="bg-yellow-50 border-yellow-200">
                        <CardContent className="p-3">
                          <div className="flex justify-between items-start mb-2">
                            <Badge variant="outline" className="text-xs">
                              {note.date ? new Date(parseInt(note.date)).toLocaleDateString('fr-FR', {
                                day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                              }) : 'Date inconnue'}
                            </Badge>
                          </div>
                          <div 
                            className="text-sm prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: note.contenu || 'Note vide' }}
                          />
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm italic">Aucune note pour ce contact</p>
                )}
              </div>

              {/* Tâches HubSpot */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold flex items-center gap-2">
                    <ListTodo className="w-5 h-5" />
                    Tâches ({contactDetails?.taches_hubspot?.length || 0})
                  </h3>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setShowAddTaskDialog(true)}
                    data-testid="add-task-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Ajouter une tâche
                  </Button>
                </div>
                {contactDetails?.taches_hubspot?.length > 0 ? (
                  <div className="space-y-2">
                    {contactDetails.taches_hubspot.map((tache, idx) => (
                      <Card key={idx} className={cn(
                        "border-l-4",
                        tache.statut === 'COMPLETED' ? 'border-l-green-500 bg-green-50' : 'border-l-orange-500 bg-orange-50'
                      )}>
                        <CardContent className="p-3">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium">{tache.titre || 'Tâche sans titre'}</p>
                              {tache.description && (
                                <p className="text-sm text-muted-foreground mt-1">{tache.description}</p>
                              )}
                            </div>
                            <Badge variant={tache.statut === 'COMPLETED' ? 'default' : 'secondary'}>
                              {tache.statut === 'COMPLETED' ? 'Terminée' : tache.statut || 'En cours'}
                            </Badge>
                          </div>
                          {tache.date_echeance && (
                            <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              Échéance: {new Date(tache.date_echeance).toLocaleDateString('fr-FR')}
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm italic">Aucune tâche pour ce contact</p>
                )}
              </div>

              {/* Stats */}
              {contactDetails?.stats && (
                <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                  <div className="text-center">
                    <p className="text-2xl font-bold">{contactDetails.stats.nb_interactions || 0}</p>
                    <p className="text-xs text-muted-foreground">Interactions</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold">{contactDetails.stats.nb_devis || 0}</p>
                    <p className="text-xs text-muted-foreground">Devis</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold">{(contactDetails.stats.ca_total || 0).toLocaleString()} €</p>
                    <p className="text-xs text-muted-foreground">CA Total</p>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContactDetail(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog Nouvelle Entreprise */}
      <Dialog open={showEntrepriseDialog} onOpenChange={setShowEntrepriseDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Nouvelle entreprise
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label>Nom de l'entreprise *</Label>
                <Input
                  value={newEntreprise.nom}
                  onChange={(e) => setNewEntreprise({...newEntreprise, nom: e.target.value})}
                  placeholder="Ex: ETA SAUVAYRE"
                  data-testid="entreprise-nom-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select 
                  value={newEntreprise.type_client}
                  onValueChange={(v) => setNewEntreprise({...newEntreprise, type_client: v})}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="prospect">Prospect</SelectItem>
                    <SelectItem value="client">Client</SelectItem>
                    <SelectItem value="fournisseur">Fournisseur</SelectItem>
                    <SelectItem value="partenaire">Partenaire</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Forme juridique</Label>
                <Select 
                  value={newEntreprise.forme_juridique}
                  onValueChange={(v) => setNewEntreprise({...newEntreprise, forme_juridique: v})}
                >
                  <SelectTrigger><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EARL">EARL</SelectItem>
                    <SelectItem value="SCEA">SCEA</SelectItem>
                    <SelectItem value="SARL">SARL</SelectItem>
                    <SelectItem value="SAS">SAS</SelectItem>
                    <SelectItem value="SA">SA</SelectItem>
                    <SelectItem value="EI">Entreprise Individuelle</SelectItem>
                    <SelectItem value="GAEC">GAEC</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>SIRET</Label>
                <Input
                  value={newEntreprise.siret}
                  onChange={(e) => setNewEntreprise({...newEntreprise, siret: e.target.value})}
                  placeholder="12345678901234"
                />
              </div>
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <Input
                  value={newEntreprise.telephone}
                  onChange={(e) => setNewEntreprise({...newEntreprise, telephone: e.target.value})}
                  placeholder="04 XX XX XX XX"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={newEntreprise.email}
                onChange={(e) => setNewEntreprise({...newEntreprise, email: e.target.value})}
                placeholder="contact@entreprise.fr"
              />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>Ville</Label>
                <Input
                  value={newEntreprise.ville}
                  onChange={(e) => setNewEntreprise({...newEntreprise, ville: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Code postal</Label>
                <Input
                  value={newEntreprise.code_postal}
                  onChange={(e) => setNewEntreprise({...newEntreprise, code_postal: e.target.value})}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEntrepriseDialog(false)}>Annuler</Button>
            <Button onClick={handleCreateEntreprise} disabled={!newEntreprise.nom} data-testid="create-entreprise-btn">
              <Plus className="w-4 h-4 mr-2" />
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog Détail Entreprise */}
      <Dialog open={showEntrepriseDetail} onOpenChange={setShowEntrepriseDetail}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className={cn(
                "w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg",
                selectedEntreprise?.type_client === 'client' ? 'bg-green-500' :
                selectedEntreprise?.type_client === 'prospect' ? 'bg-blue-500' : 'bg-gray-500'
              )}>
                <Building2 className="w-6 h-6" />
              </div>
              <div>
                <span className="text-xl">{selectedEntreprise?.nom || 'Entreprise'}</span>
                {selectedEntreprise?.forme_juridique && (
                  <p className="text-sm text-muted-foreground font-normal">{selectedEntreprise.forme_juridique}</p>
                )}
              </div>
            </DialogTitle>
          </DialogHeader>

          {loadingDetails ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Infos entreprise */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                {selectedEntreprise?.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <a href={`mailto:${selectedEntreprise.email}`} className="text-blue-600 hover:underline">
                      {selectedEntreprise.email}
                    </a>
                  </div>
                )}
                {selectedEntreprise?.telephone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <a href={`tel:${selectedEntreprise.telephone}`} className="hover:underline">
                      {selectedEntreprise.telephone}
                    </a>
                  </div>
                )}
                {selectedEntreprise?.ville && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    {[selectedEntreprise?.adresse, selectedEntreprise?.code_postal, selectedEntreprise?.ville].filter(Boolean).join(', ')}
                  </div>
                )}
                {selectedEntreprise?.siret && (
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                    SIRET: {selectedEntreprise.siret}
                  </div>
                )}
              </div>
              
              {/* Contacts de l'entreprise */}
              <div>
                <h3 className="font-semibold flex items-center gap-2 mb-3">
                  <Users className="w-5 h-5" />
                  Contacts ({entrepriseDetails?.contacts?.length || 0})
                </h3>
                {entrepriseDetails?.contacts?.length > 0 ? (
                  <div className="space-y-2">
                    {entrepriseDetails.contacts.map((contact, idx) => (
                      <Card key={idx} className="bg-blue-50 border-blue-200">
                        <CardContent className="p-3 flex items-center justify-between">
                          <div>
                            <p className="font-medium">{contact.prenom} {contact.nom}</p>
                            <p className="text-sm text-muted-foreground">{contact.poste || contact.email}</p>
                          </div>
                          {contact.telephone && (
                            <Badge variant="outline">{contact.telephone}</Badge>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm italic">Aucun contact pour cette entreprise</p>
                )}
              </div>
              
              {/* Notes */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold flex items-center gap-2">
                    <StickyNote className="w-5 h-5" />
                    Notes ({entrepriseDetails?.interactions?.filter(i => i.type_interaction === 'note').length || 0})
                  </h3>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setShowAddNoteDialog(true)}
                    data-testid="add-note-entreprise-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Ajouter une note
                  </Button>
                </div>
                {entrepriseDetails?.interactions?.filter(i => i.type_interaction === 'note').length > 0 ? (
                  <div className="space-y-3">
                    {entrepriseDetails.interactions.filter(i => i.type_interaction === 'note').map((note, idx) => (
                      <Card key={idx} className="bg-yellow-50 border-yellow-200">
                        <CardContent className="p-3">
                          <div className="flex justify-between items-start mb-2">
                            <Badge variant="outline" className="text-xs">
                              {new Date(note.date_interaction || note.created_at).toLocaleDateString('fr-FR', {
                                day: 'numeric', month: 'short', year: 'numeric'
                              })}
                            </Badge>
                          </div>
                          <p className="text-sm">{note.description || note.sujet}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm italic">Aucune note pour cette entreprise</p>
                )}
              </div>
              
              {/* Tâches */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold flex items-center gap-2">
                    <ListTodo className="w-5 h-5" />
                    Tâches ({entrepriseDetails?.interactions?.filter(i => i.est_tache).length || 0})
                  </h3>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setShowAddTaskDialog(true)}
                    data-testid="add-task-entreprise-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Ajouter une tâche
                  </Button>
                </div>
                {entrepriseDetails?.interactions?.filter(i => i.est_tache).length > 0 ? (
                  <div className="space-y-2">
                    {entrepriseDetails.interactions.filter(i => i.est_tache).map((tache, idx) => (
                      <Card key={idx} className={cn(
                        "border-l-4",
                        tache.statut_tache === 'termine' ? 'border-l-green-500 bg-green-50' : 'border-l-orange-500 bg-orange-50'
                      )}>
                        <CardContent className="p-3">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium">{tache.sujet}</p>
                              {tache.description && (
                                <p className="text-sm text-muted-foreground mt-1">{tache.description}</p>
                              )}
                            </div>
                            <Badge variant={tache.statut_tache === 'termine' ? 'default' : 'secondary'}>
                              {tache.statut_tache === 'termine' ? 'Terminée' : tache.statut_tache === 'en_cours' ? 'En cours' : 'À faire'}
                            </Badge>
                          </div>
                          {tache.date_echeance && (
                            <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              Échéance: {new Date(tache.date_echeance).toLocaleDateString('fr-FR')}
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm italic">Aucune tâche pour cette entreprise</p>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEntrepriseDetail(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog Ajouter Note */}
      <Dialog open={showAddNoteDialog} onOpenChange={setShowAddNoteDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <StickyNote className="w-5 h-5 text-yellow-500" />
              Ajouter une note
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Contenu de la note *</Label>
              <Textarea
                value={newNote.contenu}
                onChange={(e) => setNewNote({...newNote, contenu: e.target.value})}
                placeholder="Saisissez votre note ici..."
                rows={6}
                data-testid="note-content-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddNoteDialog(false)}>Annuler</Button>
            <Button 
              onClick={handleAddNote} 
              disabled={!newNote.contenu.trim() || savingNote}
              data-testid="save-note-btn"
            >
              {savingNote ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Enregistrer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog Ajouter Tâche */}
      <Dialog open={showAddTaskDialog} onOpenChange={setShowAddTaskDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListTodo className="w-5 h-5 text-orange-500" />
              Ajouter une tâche
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Titre de la tâche *</Label>
              <Input
                value={newTask.titre}
                onChange={(e) => setNewTask({...newTask, titre: e.target.value})}
                placeholder="Ex: Rappeler le client, Envoyer le devis..."
                data-testid="task-title-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={newTask.description}
                onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                placeholder="Détails de la tâche (optionnel)"
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date d'échéance</Label>
                <Input
                  type="date"
                  value={newTask.date_echeance}
                  onChange={(e) => setNewTask({...newTask, date_echeance: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Priorité</Label>
                <Select 
                  value={newTask.priorite}
                  onValueChange={(v) => setNewTask({...newTask, priorite: v})}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basse">Basse</SelectItem>
                    <SelectItem value="normale">Normale</SelectItem>
                    <SelectItem value="haute">Haute</SelectItem>
                    <SelectItem value="urgente">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddTaskDialog(false)}>Annuler</Button>
            <Button 
              onClick={handleAddTask} 
              disabled={!newTask.titre.trim() || savingTask}
              data-testid="save-task-btn"
            >
              {savingTask ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Création...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Créer la tâche
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
